import React, {useState, useEffect} from "react";
import uniqBy from "ramda/src/uniqBy";
import prop from "ramda/src/prop";

import { IExtendedL3, Network } from "../../services/NetworkApi";

import Switch from '@mui/material/Switch';
import { L2Selector } from "../../common/network/NetworkSelectors";
import { Grid } from "../../UI/Grid";
import { TableHeader } from "../TableHeader";
import { Typography } from "../../UI/Typography";
import { Table } from "../../UI/Table";
import { COLOR_WHITE, COLOR_WELL_READ } from "../../constants/styles";
import { TableHead } from "../../UI/TableHead";
import { TableRow } from "../../UI/TableRow";
import { TableCell } from "../../UI/TableCell";
import { HelpTooltip } from "../HelpTooltip";
import { EnhancedTableBody } from "../EnhancedTableBody";
import { ReadOnlyWrapper } from "../ReadOnlyWrapper";
import { Box } from "../../UI/Box";
import { MaxNumberInput } from "../MaxNumberInput";
import { TablePagination } from "../TablePagination";
import { useTablePaginationContext } from "../TablePagination/context";
import { usePaginationHandler } from "../../hooks/usePaginationHandler";
import { useMatchingRulesHandler } from "../../hooks/useMatchingRulesHandler";

const MATCH_GROUP_MAX_HELP_TITLE = "Match Groups - Max";
const MATCH_GROUP_MAX_HELP_CONTENT = `Enter the maximum number of match groups that can be created in RoomSync. A match group is defined as 2 or more users matched as roommates. After the max is reached, all users must join an existing match group. You can update this match group max at any time. If the max is not set, there will be no limit on the number of match groups that can form. We recommend setting the max to the number of front doors you have of this unit type.`;

const MATCH_GROUP_CURRENT_HELP_TITLE = "Match Groups - Current";
const MATCH_GROUP_CURRENT_HELP_CONTENT =
  "This is how many match groups currently exist in RoomSync. A match group is 2 or more users matched as roommates.";

const MATCH_GROUP_AVAILABLE_HELP_TITLE = "Match Groups - Available";
const MATCH_GROUP_AVAILABLE_HELP_CONTENT =
  "This is the number of available match groups remaining. It is calculated as Max minus Current. A negative number (shown in red) indicates that the current number of match groups exceeds the maximum allowed.";

export const MaxMatchGroups: React.FC = () => {
  const [ showArchived, setShowArchived ] = useState<boolean>(false);
  const {data, setData, isLoading } = useMatchingRulesHandler({ includeCount: true });
  const [subnetwork, setSubnetwork] = React.useState<number>(0);
  
  const [ filteredData, setFilteredData ] = useState<IExtendedL3[]>([]);
  const { page, setPage, perPage, setPages } = useTablePaginationContext();
  const { selectedRows, pages } = usePaginationHandler(
    filteredData,
    page,
    perPage
  );

  useEffect(() => {
    const uniqueData = uniqBy(prop('key'), data);
    const newData = uniqueData
      .filter(row => showArchived? row : row.matchingRoundStatus!== "ARCHIVED")
      .filter(row => subnetwork === 0 || !subnetwork ? row: row.l2Id === subnetwork);
    setFilteredData(newData);

  }, [showArchived, subnetwork, data]);

  const isReadOnly = (row: any) => (row?.matchingRoundStatus === "ARCHIVED" || row?.matchingRoundStatus === "CLOSED");
  const l2Filter = (l2: Network) => (l2.matchingRoundStatus !== "ARCHIVED");
  const handleTogglChange = (_: any, checked: boolean) =>setShowArchived(checked);
    

  React.useEffect(() => {
    setPages(pages);
    setPage(1);
  }, [pages.length]);

  return (
    <>
      <TableHeader 
       py={5}
        endAdornment={
        <Grid container justifyContent="flex-end" alignItems="center">            
            <L2Selector
              onSelectHandler={setSubnetwork}
              variant="table"
              defaultValue={0}
              filter={!showArchived? l2Filter : undefined}
              extraMenuOptions={[{ value: 0, label: "All Matching Rounds" }]}
            />
          <Typography color={COLOR_WHITE}>
             Show Archived
            <Switch value={showArchived} onChange={handleTogglChange}/>
          </Typography>
        </Grid>
        }>
        <Typography fontWeight={600} color={COLOR_WHITE}>
          Maximum Match Groups
        </Typography>
      </TableHeader>
      <Table>
        <TableHead secondary>
          <TableRow>
            <TableCell>Unit Type</TableCell>
            <TableCell>No. of Users Allowed in a Match Group</TableCell>
            <TableCell>
              <HelpTooltip
                title={MATCH_GROUP_MAX_HELP_TITLE}
                text={MATCH_GROUP_MAX_HELP_CONTENT}
              >
                No. of Match Groups - Max
              </HelpTooltip>
            </TableCell>
            <TableCell>
              <HelpTooltip
                title={MATCH_GROUP_CURRENT_HELP_TITLE}
                text={MATCH_GROUP_CURRENT_HELP_CONTENT}
              >
                No. of Match Groups - Current
              </HelpTooltip>
            </TableCell>
            <TableCell>
              <HelpTooltip
                title={MATCH_GROUP_AVAILABLE_HELP_TITLE}
                text={MATCH_GROUP_AVAILABLE_HELP_CONTENT}
              >
                No. of Match Groups - Available
              </HelpTooltip>
            </TableCell>
          </TableRow>
        </TableHead>
        <EnhancedTableBody colSpan={5} isLoading={isLoading}>
          {selectedRows.map((row) => {
            const available = row.maxNumberOfMatches != null
              ? row.maxNumberOfMatches - (row.current || 0)
              : null;
            const hasMax = !!row.maxNumberOfMatches && row.maxNumberOfMatches > 0;
            const isNegative = available != null && available < 0;

            return (
              <TableRow key={row.key}>
                <TableCell>
                  {row.l2name} | {row.name}
                </TableCell>
                <TableCell>{row.size + 1}</TableCell>
                <TableCell>
                  <Box maxWidth={74}>
                    <ReadOnlyWrapper>
                      <MaxNumberInput
                        updateData={setData}
                        value={row.maxNumberOfMatches}
                        l2Id={row.l2Id}
                        l3Id={row.l3Id}
                        isDisabled={isReadOnly(row)}
                      />
                    </ReadOnlyWrapper>
                  </Box>
                </TableCell>
                <TableCell>{row.current}</TableCell>
                <TableCell>
                  {available != null && (
                    <Typography
                      variant="body2"
                      sx={{ color: hasMax && isNegative ? COLOR_WELL_READ : 'inherit' }}
                    >
                      {!hasMax && isNegative ? '-' : available}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </EnhancedTableBody>
      </Table>
      <TablePagination />
    </>
  );
};
